import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PriceChart } from "../components/PriceChart";

interface Entry {
  date: string;
  created_at: string;
  gold_sell_hkd: number | null;
  gold_buy_hkd: number | null;
  platinum_sell_hkd: number | null;
  platinum_buy_hkd: number | null;
  grain_sell_hkd: number | null;
  grain_buy_hkd: number | null;
}

interface EntriesResponse {
  entries: Entry[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const PER_PAGE = 15;

export function AdminPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.removeItem("gold_admin_password");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/me", { credentials: "include" });
        if (!cancelled && res.ok) setIsLoggedIn(true);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchEntries = useCallback(async (p: number) => {
    setLoadingEntries(true);
    try {
      const res = await fetch(
        `/api/admin/entries?page=${p}&per_page=${PER_PAGE}`,
        { credentials: "include", cache: "no-store" }
      );
      if (res.status === 401) {
        setIsLoggedIn(false);
        return;
      }
      if (!res.ok) return;
      const json = (await res.json()) as EntriesResponse;
      setEntries(json.entries);
      setPage(json.page);
      setTotalPages(json.total_pages);
      setTotal(json.total);
    } catch {
      /* ignore */
    } finally {
      setLoadingEntries(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    void fetchEntries(1);
  }, [isLoggedIn, fetchEntries]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: password.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error === "invalid_password"
            ? "密碼錯誤"
            : "登入失敗"
        );
      }
      setPassword("");
      setIsLoggedIn(true);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "登入失敗");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/admin/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore */
    }
    setIsLoggedIn(false);
    setMessage(null);
  };

  const goPage = (p: number) => {
    if (p < 1 || p > totalPages) return;
    void fetchEntries(p);
  };

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("zh-HK", {
        timeZone: "Asia/Hong_Kong",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return iso;
    }
  };

  if (!sessionChecked) {
    return (
      <div className="page page-admin">
        <p>載入中…</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="page page-admin">
        <h1 className="page-title">後台登入</h1>
        <form className="card" onSubmit={handleLogin}>
          <label className="field">
            <span>密碼</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button type="submit" className="btn-primary">
            登入
          </button>
          {message && (
            <div style={{ marginTop: 12, fontSize: 14, color: "#fca5a5" }}>
              {message}
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="page page-admin">
      <div className="admin-top-bar">
        <h1 className="page-title" style={{ margin: 0 }}>
          後台管理
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate("/admin/entry")}
          >
            ＋ 新增一筆
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void handleLogout()}
          >
            登出
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="entries-header">
          <span style={{ fontWeight: 600 }}>更新記錄</span>
          <span className="entries-count">共 {total} 筆</span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => void fetchEntries(page)}
          >
            重新整理
          </button>
        </div>

        {loadingEntries ? (
          <p style={{ textAlign: "center", padding: 20 }}>載入中…</p>
        ) : entries.length === 0 ? (
          <p style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>
            尚無記錄
          </p>
        ) : (
          <>
            <div className="entries-list">
              {entries.map((entry) => {
                const fmt = (v: number | null) =>
                  v != null ? v.toLocaleString() : "—";
                return (
                  <button
                    key={`${entry.date}-${entry.created_at}`}
                    type="button"
                    className="entry-row-full"
                    onClick={() =>
                      navigate(
                        `/admin/entry?date=${encodeURIComponent(entry.date)}&created_at=${encodeURIComponent(entry.created_at)}`
                      )
                    }
                  >
                    <span className="entry-date">{entry.date}</span>
                    <span className="entry-time">
                      {formatTime(entry.created_at)}
                    </span>
                    <span className="entry-prices">
                      <span className="ep-group">
                        <span className="ep-label">金</span>
                        <span className="ep-val">{fmt(entry.gold_sell_hkd)}</span>
                        <span className="ep-sep">/</span>
                        <span className="ep-val">{fmt(entry.gold_buy_hkd)}</span>
                      </span>
                      <span className="ep-group">
                        <span className="ep-label">鉑</span>
                        <span className="ep-val">{fmt(entry.platinum_sell_hkd)}</span>
                        <span className="ep-sep">/</span>
                        <span className="ep-val">{fmt(entry.platinum_buy_hkd)}</span>
                      </span>
                      <span className="ep-group">
                        <span className="ep-label">粒</span>
                        <span className="ep-val">{fmt(entry.grain_sell_hkd)}</span>
                        <span className="ep-sep">/</span>
                        <span className="ep-val">{fmt(entry.grain_buy_hkd)}</span>
                      </span>
                    </span>
                    <span className="entry-arrow">›</span>
                  </button>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => goPage(page - 1)}
                >
                  ‹ 上一頁
                </button>
                <span className="pagination-info">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => goPage(page + 1)}
                >
                  下一頁 ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2 className="page-title" style={{ marginTop: 0, fontSize: 16 }}>
          最近 7 天黃金賣出走勢
        </h2>
        <PriceChart />
      </div>
    </div>
  );
}
